import { useCallback, useSyncExternalStore } from 'react';

export type LayoutMode = 'mobile' | 'desktop';
export type LayoutModeOverride = LayoutMode | 'system';

const layoutMediaQuery = '(pointer: fine) and (hover: hover) and (min-width: 768px)';
const layoutOverrideStorageKey = 'chartify.layoutMode';
const layoutModeChangeEvent = 'chartify:layout-mode-change';

interface LayoutModeState {
  mode: LayoutMode;
  override: LayoutModeOverride;
  setOverride(override: LayoutModeOverride): void;
}

export function useLayoutMode(): LayoutModeState {
  const mode = useSyncExternalStore(subscribeToLayoutMode, getLayoutModeSnapshot, getServerLayoutModeSnapshot);
  const override = useSyncExternalStore(subscribeToLayoutMode, getLayoutOverrideSnapshot, getServerLayoutOverrideSnapshot);
  const setOverride = useCallback((nextOverride: LayoutModeOverride) => {
    setLayoutModeOverride(nextOverride);
  }, []);

  return { mode, override, setOverride };
}

export function getLayoutModeOverride(): LayoutModeOverride {
  if (typeof window === 'undefined') return 'system';
  return parseLayoutModeOverride(window.localStorage.getItem(layoutOverrideStorageKey));
}

export function setLayoutModeOverride(override: LayoutModeOverride): void {
  if (typeof window === 'undefined') return;
  if (override === 'system') {
    window.localStorage.removeItem(layoutOverrideStorageKey);
  } else {
    window.localStorage.setItem(layoutOverrideStorageKey, override);
  }
  window.dispatchEvent(new Event(layoutModeChangeEvent));
}

function subscribeToLayoutMode(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const media = window.matchMedia(layoutMediaQuery);
  const notify = () => onStoreChange();
  media.addEventListener('change', notify);
  window.addEventListener('storage', notify);
  window.addEventListener(layoutModeChangeEvent, notify);

  return () => {
    media.removeEventListener('change', notify);
    window.removeEventListener('storage', notify);
    window.removeEventListener(layoutModeChangeEvent, notify);
  };
}

function getLayoutModeSnapshot(): LayoutMode {
  const override = getLayoutModeOverride();
  if (override !== 'system') return override;
  if (typeof window === 'undefined') return 'mobile';
  return window.matchMedia(layoutMediaQuery).matches ? 'desktop' : 'mobile';
}

function getServerLayoutModeSnapshot(): LayoutMode {
  return 'mobile';
}

function getLayoutOverrideSnapshot(): LayoutModeOverride {
  return getLayoutModeOverride();
}

function getServerLayoutOverrideSnapshot(): LayoutModeOverride {
  return 'system';
}

function parseLayoutModeOverride(value: string | null): LayoutModeOverride {
  if (value === 'mobile' || value === 'desktop') return value;
  return 'system';
}
